import React from 'react';
import { Card, CardContent, CardHeader, List, ListItem, ListItemAvatar, ListItemText, Avatar, Box, Typography, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddBoxIcon from '@mui/icons-material/AddBox';
import TokenIcon from '@mui/icons-material/Token';

const ActivityFeed = ({ activities }) => {
  const theme = useTheme();

  const getActivityIcon = (type) => {
    switch (type) {
      case 'verification':
        return <VerifiedUserIcon />;
      case 'user_registration':
        return <PersonAddIcon />;
      case 'product_added':
        return <AddBoxIcon />;
      case 'nft_minted':
        return <TokenIcon />;
      default:
        return <VerifiedUserIcon />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'verification':
        return theme.palette.success.main;
      case 'user_registration':
        return theme.palette.info.main;
      case 'product_added':
        return theme.palette.primary.main;
      case 'nft_minted':
        return theme.palette.secondary.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getActivityLabel = (type) => {
    switch (type) {
      case 'verification':
        return 'Verification';
      case 'user_registration':
        return 'New User';
      case 'product_added':
        return 'New Product';
      case 'nft_minted':
        return 'NFT Minted';
      default:
        return 'Activity';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  return (
    <Card>
      <CardHeader
        title="Recent Activity"
        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0, maxHeight: 400, overflowY: 'auto' }}>
        <List disablePadding>
          {activities.map((activity, index) => (
            <ListItem
              key={activity.id}
              alignItems="flex-start"
              sx={{
                px: 0,
                py: 1.5,
                borderBottom: index < activities.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                '&:last-child': {
                  borderBottom: 'none'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar
                  sx={{
                    backgroundColor: getActivityColor(activity.type),
                    width: 36,
                    height: 36
                  }}
                >
                  {getActivityIcon(activity.type)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="body2" fontWeight={500}>
                      {activity.message}
                    </Typography>
                    <Chip
                      label={getActivityLabel(activity.type)}
                      size="small"
                      sx={{
                        backgroundColor: `${getActivityColor(activity.type)}20`,
                        color: getActivityColor(activity.type),
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        height: 20
                      }}
                    />
                  </Box>
                }
                secondary={
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {activity.location}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      {formatTime(activity.timestamp)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;